<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <v-container fluid v-if="sessionActive" class="pa-3">
        <material-header
            title="Panelist manager"
            info="Find, edit and perform batch actions on panelists"
            help="panelist-manager"
            icon="mdi-account-group">

            <template slot="buttons">

                <v-tooltip color="primary" left v-if="hasPermission('view panelists')">
                    <template v-slot:activator="{ on }">
                        <v-btn @click="reloadPanelists()" v-on="on" icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>Reload data</span>
                </v-tooltip>

                <v-tooltip top color="primary">
                    <template v-slot:activator="{ on }">
                        <v-btn :disabled="!hasPermission('view panelists')"
                               @click="selectColumnsClicked()"
                               v-on="on"
                               color="primary"
                               fab
                               small
                               class="elevation-0"
                        >
                            <v-icon>mdi-format-columns</v-icon>
                        </v-btn>
                    </template>
                    <span>Select columns</span>
                </v-tooltip>

            </template>

        </material-header>

        <v-alert v-if="!hasPermission('view panelists')"
                 class="body-2"
                 type="error"
        >
            Your user account doesn't have sufficient permissions for this feature
        </v-alert>

        <v-row v-if="hasPermission('view panelists')">
            <v-col cols="12" class="pt-0 pb-0">
                <v-card flat v-if="hasPermission('view panelists')">

                    <div class="pa-2 pt-5 pb-0 d-flex" v-if="warningsExist && !warningAlertDismissed">
                        <v-alert icon="mdi-alert"
                                 transition="scale-transition"
                                 dismissible
                                 type="warning"
                                 dense
                                 @input="closeWarning"
                                 class="body-2 flex-shrink-1">
                            {{ warningsText }}
                        </v-alert>
                    </div>

                    <v-card-text class="d-flex flex-row pt-2 pb-2">

                        <filter-selector
                            v-if="!searchActive"
                            @change="filtersChanged"
                            :filters="filters"
                            :exclusive="true"
                            class="align-self-center"
                        ></filter-selector>

                        <div class="flex-grow-1"></div>

                        <v-text-field
                            v-if="!filterActive && hasPermission('view panelists')"
                            v-model="searchFieldModel"
                            class="align-self-center mr-4"
                            placeholder="Search by id, name or email"
                            @keyup.enter="runSearch()"
                            :loading="panelistsLoading"
                            clearable
                            hide-details
                            prepend-icon="mdi-magnify"
                            @click:clear="searchCleared"
                            solo
                            dense
                            background-color="grey darken-2"
                            style="max-width: 400px;"
                        ></v-text-field>

                        <stat-box
                            style="width: 300px"
                            class="align-self-center"
                            :value="meta.available"
                            hint="# of matches"
                            color="primary"
                            :loading="panelistsLoading"
                        ></stat-box>

                    </v-card-text>
                </v-card>
            </v-col>

            <!-- DATA TABLE -->
            <v-col cols="12" class="pt-0">

                <v-data-table
                    v-model="selected"
                    :headers="headers"
                    :items="items"
                    item-key="id"
                    class="elevation-2"
                    :loading="panelistsLoading"
                    :show-select="hasPermission('update panelists') || hasPermission('delete panelists')"
                    :rows-per-page.sync="tableItemsPerPage"
                    :footer-props="footerProps"
                    :hide-default-footer="true"
                >

                    <!-- PANELIST NOT FOUND -->
                    <template v-slot:no-data>
                        <v-alert v-if="(searchActive || filterActive) && !panelistsLoading && items.length === 0"
                                 type="error"
                                 class="body-2 text-left mt-3"
                                 icon="mdi-information">
                            No panelists found!
                        </v-alert>
                        <v-alert v-if="!searchActive && !filterActive && !panelistsLoading && items.length === 0"
                                 color="info"
                                 prominent
                                 class="body-2 text-left mt-3"
                                 icon="mdi-information">
                            Enter panelist ID, name or email into the search box or use filters to find panelists
                        </v-alert>
                    </template>

                    <template v-slot:item="{ item, isSelected, select }">
                        <tr>
                            <td class="text-left"
                                v-show="hasPermission('update panelists') || hasPermission('delete panelists')"
                                style="width: 1px">
                                <v-checkbox
                                    class="mt-1"
                                    color="primary"
                                    hide-details
                                    :value="isSelected"
                                    @change="select"
                                    @click.capture.stop=""
                                ></v-checkbox>
                            </td>

                            <td class="text-left" style="width: 1px">
                                <v-tooltip top color="primary">
                                    <template v-slot:activator="{ on }">
                                        <v-icon dark color="primary" v-on="on" @click="infoIconClicked(item.id)">
                                            mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span class="body-2">
                                        <div>Panelist's unique ID</div>
                                        <div>{{ item.id }}</div>
                                    </span>
                                    <span class="caption grey--text text--lighten-4">
                                        (Click icon to copy)
                                    </span>
                                </v-tooltip>
                            </td>

                            <td v-for="variable in selectedVariables"
                                :key="variable.uuid"
                                class="text-left cursor-pointer no-wrap"
                                @click="hasPermission('view panelists') ? editPanelist(item.id) : null">

                                <div v-if="!item.hasOwnProperty(variable.uuid)">
                                    <v-chip pill :value="true" color="red lighten-1">
                                        <v-icon small class="pr-2">mdi-alert-outline</v-icon>
                                        Missing
                                    </v-chip>
                                </div>

                                <div v-if="item.hasOwnProperty(variable.uuid) && item[variable.uuid] === null">
                                    <v-chip pill color="grey darken-3">
                                        <v-icon small class="pr-2">mdi-information</v-icon>
                                        Redacted PI
                                    </v-chip>
                                </div>

                                <div v-else>
                                    <div v-if="variable.type!=='checkbox'">
                                        {{ item[variable.uuid] }}
                                    </div>
                                    <div v-else>
                                        <v-tooltip color="primary" left>
                                            <template v-slot:activator="{ on }">
                                                <v-chip v-on="on" color="primary">
                                                    <v-icon small class="pr-2">mdi-view-list</v-icon>
                                                    Multiple values
                                                </v-chip>
                                            </template>
                                            <span>
                                                <v-list dense>
                                                    <v-list-item v-for="value in item[variable.uuid]"
                                                                 :key="value">
                                                        <v-list-item-avatar>
                                                            <v-icon>mdi-check-box-outline</v-icon>
                                                        </v-list-item-avatar>
                                                        <v-list-item-content>
                                                            {{ value }}
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                </v-list>
                                            </span>
                                        </v-tooltip>
                                    </div>
                                </div>
                            </td>

                            <!-- ACTIONS -->
                            <td class="pl-2">
                                <div style="white-space: nowrap;">

                                    <v-tooltip top color="primary">
                                        <template v-slot:activator="{ on }">
                                            <v-btn :disabled="!hasPermission('delete panelists')"
                                                   class="ma-0"
                                                   icon
                                                   v-on="on"
                                                   @click="deletePanelistClicked(item.id)"
                                            >
                                                <v-icon light small color="white">mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Delete panelist</span>
                                    </v-tooltip>
                                </div>
                            </td>

                        </tr>
                    </template>

                </v-data-table>

            </v-col>

        </v-row>

        <!-- FLOATING ACTION BUTTON FOR BATCH FILTER ACTION -->
        <v-tooltip left color="primary">
            <template v-slot:activator="{ on }">
                <v-btn
                    v-if="hasPermission('delete panelists') || hasPermission('update panelists') || hasPermission('export panelists')"
                    v-show="filterActive && selectedRows.length === 0"
                    color="primary"
                    dark
                    fixed
                    bottom
                    right
                    fab
                    v-on="on"
                    @click="batchActionFilterClicked"
                >
                    <v-icon>mdi-account-convert</v-icon>
                </v-btn>
            </template>
            <span>Perform batch action on all found panelists</span>
        </v-tooltip>

        <!-- FLOATING ACTION BUTTON FOR SELECTED BATCH ACTION -->
        <v-tooltip left color="primary">
            <template v-slot:activator="{ on }">
                <v-btn
                    v-if="hasPermission('delete panelists') || hasPermission('update panelists')"
                    v-show="selectedRows.length > 0"
                    color="primary"
                    dark
                    fixed
                    bottom
                    right
                    fab
                    v-on="on"
                    @click="batchActionSelectedClicked"
                >
                    <v-icon>mdi-account-convert</v-icon>
                </v-btn>
            </template>
            <span>Perform batch action on selected panelists</span>
        </v-tooltip>

        <!-- BATCH ACTION DIALOG -->
        <batch-action-dialog
            v-model="batchActionDialogModel"
            :affected-count="batchActionAffectedCount"
            :scope="batchActionScope"
            :actions="availableActions"
            @action="batchAction"
        ></batch-action-dialog>

        <!-- COLUMN PICKER -->
        <data-variable-picker
            v-model="dataVariablePickerModel"
            :data-variables="dataVariables"
            :multiple="true"
            :selected="selectedVariables"
            @change="columnsSelected"
        ></data-variable-picker>

        <!-- DELETE PANELIST CONFIRMATION DIALOG -->
        <confirmation-dialog
            v-model="confirmationDialogModel"
            headline="Delete panelist?"
            description="Are you sure you want to delete this panelist? This operation cannot be undone!"
            @selected="confirmationDialogEntry"
            delete
        ></confirmation-dialog>

        <!-- DEBUG FOR DATA -->
        <v-col cols="12" v-if="debug">
            <v-card color="blue-grey darken-4">
                <v-card-title>
                    <v-icon large class="mr-2">mdi-bug</v-icon>
                    <h3>Debug</h3>
                </v-card-title>
                <v-card-text>
                    <json :data="availableActions"></json>
                </v-card-text>
            </v-card>
        </v-col>

    </v-container>
</template>

<script>

import MaterialHeader from '../../../components/UI/MaterialHeader';
import Json from 'vue-json-pretty';
import Store from '../store';
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import {clipboardMixin} from '../../../components/mixins/clipboardMixin';
import StatBox from "../../../components/UI/StatBoxNumerical";
import DataVariablePicker from '../../../components/UI/DataVariablePicker';
import ConfirmationDialog from '../../../components/UI/ConfirmationDialog';
import FilterSelector from '../../../components/UI/Filter';
import BatchActionDialog from './BatchActionDialog';

export default {

    mixins: [activeSessionChecker, clipboardMixin],

    components: {
        Json,
        StatBox,
        MaterialHeader,
        DataVariablePicker,
        ConfirmationDialog,
        FilterSelector,
        BatchActionDialog
    },

    data() {
        return {
            debug: false,
            filterSpeedDialModel: true,
            confirmationDialogModel: false,
            deletePanelistId: null,
            dataVariablePickerModel: false,
            batchActionDialogModel: false,
            batchActionAffectedCount: 0,
            batchActionScope: null,
            footerProps: {
                itemsPerPageOptions: [100]
            },
            warningAlertDismissed: true,
            createPasswordBatchActionEnabled: true,
        }
    },

    computed: {

        // Root state
        ...mapState([
            'dataVariables',
        ]),

        ...mapState('SubPanels', [
            'subPanels'
        ]),

        ...mapState('PanelistManager', [
            'panelists',
            'panelistsLoading',
            'meta',
            'search',
            'searchActive',
            'selectedVariables',
            'headers',
            'itemsPerPage',
            'filters',
            'selectedRows',
            'actions',
        ]),

        ...mapGetters('PanelistManager', [
            'items',
            'filterActive'
        ]),

        tableItemsPerPage: {
            get() {
                return this.itemsPerPage;
            },
            set(rows) {
                this.setItemsPerPage(rows);
            }
        },

        searchFieldModel: {
            get() {
                return this.search.term;
            },
            set(value) {
                this.updateSearch({
                    size: 100,
                    term: value,
                    ids: this.selectedVariables.map(variable => variable.uuid)
                });
            }
        },

        selected: {
            get() {
                return this.selectedRows;
            },
            set(rows) {
                this.setSelectedRows(rows);
            }
        },

        defaultSelected() {

            if (this.hasPermission('view pi')) {
                return [
                    'FIRST_NAME',
                    'LAST_NAME',
                    'EMAIL',
                    'EMAIL_VALID',
                    'STATUS',
                    'INVITED_COUNT',
                    'COMPLETED_COUNT'
                ];
            } else {
                return [
                    'EMAIL_VALID',
                    'STATUS',
                    'SUSPICIOUS',
                    'INVITED_COUNT',
                    'STARTED_COUNT',
                    'COMPLETED_COUNT',
                    'POINTS_BALANCE'
                ];
            }
        },

        warningsExist() {
            if (!this.hasPermission('update panelists')) return true;
            if (!this.hasPermission('view pi')) return true;
        },

        warningsText() {

            let warnings = [];

            if (!this.hasPermission('update panelists')) {
                warnings.push("update panelists");
            }
            if (!this.hasPermission('view pi')) {
                warnings.push("view Personally Identifiable (PI) information");
            }

            return "Your account does not have permissions to " + warnings.join(' and ');
        },

        availableActions() {
            if (this.subPanels.length > 0) {

                let actions = JSON.parse(JSON.stringify(this.actions));

                actions.push({
                    name: 'Change sub panel(s) membership',
                    type: 'subpanel',
                    permission: 'update panelists',
                    icon: 'mdi-account-arrow-right',
                    options: {
                        values: this.subPanels.map(subPanel => {
                            return {name: subPanel.name, id: subPanel.id};
                        }),
                        label: 'Select target sub panel',
                    },
                    selected: null,
                });

                if (this.createPasswordBatchActionEnabled) {
                    actions.push({
                        name: 'Send create password email',
                        type: 'create-email',
                        permission: 'update panelists',
                        icon: 'mdi-lock-reset',
                        selected: null,
                    });
                }

                return actions.filter(action => {
                    if (action.role) return this.hasRole(action.role);
                    return this.hasPermission(action.permission);
                });
            }
            return [];
        }
    },

    methods: {

        // Root store
        ...mapActions([
            'getDataVariables'
        ]),

        ...mapActions('PanelistManager', [
            'searchPanelists',
            'filterPanelists',
            'deleteProfile',
            'deleteByIds',
            'deleteByFilter',
            'changeDataVariableByIds',
            'changeDataVariableByFilter',
            'changeSubPanelsByIds',
            'changeSubPanelsByFilter',
            'exportPanelistsByIds',
            'exportPanelistsByFilter',
            'sendCreatePasswordEmails'
        ]),

        ...mapMutations('PanelistManager', [
            'updateSearch',
            'setSelectedVariables',
            'setHeaders',
            'setPanelists',
            'setPanelistsLoading',
            'clearMeta',
            'setSearchActive',
            'setItemsPerPage',
            'setFilters',
            'clearActiveFilters',
            'setSelectedRows'
        ]),

        batchActionFilterClicked() {
            this.batchActionDialogModel = true;
            this.batchActionScope = 'filter';
            this.batchActionAffectedCount = this.meta.available;
        },

        batchActionSelectedClicked() {
            this.batchActionDialogModel = true;
            this.batchActionScope = 'selected';
            this.batchActionAffectedCount = this.selectedRows.length;
        },

        batchAction(action) {
            if (this.batchActionScope === 'selected') {
                this.batchActionSelected(action);
            } else if (this.batchActionScope === 'filter') {
                this.batchActionFilter(action);
            }
        },

        batchActionSelected(action) {

            if (action.type === 'delete') {

                this.deleteByIds()
                    .then(() => {
                        Bus.$emit('flash-message', {message: 'Panelists successfully deleted'});
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {message: 'Delete failed!', type: 'error'});
                    });

            } else if (action.type === 'subpanel') {

                this.changeSubPanelsByIds(action.selected.id)
                    .then(() => {
                        Bus.$emit('flash-message', {message: 'Sub Panel memberships updated'});
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {
                            message: 'Sub Panel membership update failed!',
                            type: 'error'
                        });
                    });

            } else if (action.type === 'status') {

                const dataVariable = this.dataVariables.find(variable => variable.name === 'STATUS');

                const payload = {
                    dataVariableId: dataVariable.id,
                    value: action.selected.value
                };

                this.changeDataVariableByIds(payload)
                    .then(() => {
                        Bus.$emit('flash-message', {message: 'Panelists statuses updated'});
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {message: 'Panelists status update failed!', type: 'error'});
                    });

            } else if (action.type === 'export') {

                const payload = {
                    decode: action.selected.value
                };

                this.exportPanelistsByIds(payload)
                    .then(() => {
                        Bus.$emit('flash-message', {message: 'Panelists statuses updated'});
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {message: 'Panelists status update failed!', type: 'error'});
                    });
            }

            this.setSelectedRows([]);
        },

        batchActionFilter(action) {
            if (action.type === 'delete') {

                this.deleteByFilter()
                    .then(() => {
                        Bus.$emit('flash-message', {
                            duration: 10000,
                            message:
                                'Delete request has been submitted! '
                        });
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {message: 'Delete request failed!', type: 'error'});
                    });

            } else if (action.type === 'subpanel') {

                this.changeSubPanelsByFilter(action.selected.id)
                    .then(() => {
                        Bus.$emit('flash-message', {
                            duration: 10000,
                            message: 'Sub Panel(s) membership update request has been submitted!'
                        });
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {
                            message: 'Sub Panel membership update request failed!',
                            type: 'error'
                        });
                    });

            } else if (action.type === 'status') {

                const dataVariable = this.dataVariables.find(variable => variable.name === 'STATUS');

                const payload = {
                    dataVariableId: dataVariable.id,
                    value: action.selected.value
                };

                this.changeDataVariableByFilter(payload)
                    .then(() => {
                        Bus.$emit('flash-message', {
                            duration: 10000,
                            message: 'Status change request has been submitted!'

                        });
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {message: 'Status change request failed!', type: 'error'});
                    });

            } else if (action.type === 'export') {

                const payload = {
                    decode: action.selected.value
                };

                this.exportPanelistsByFilter(payload)
                    .then(() => {
                        Bus.$emit('flash-message', {
                            duration: 10000,
                            message:
                                'Export is now starting! Final export file will be placed in the downloads.'
                        });
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {message: 'Export panelists failed!', type: 'error'});
                    });

            } else if (action.type === 'create-email') {

                this.sendCreatePasswordEmails()
                    .then(() => {
                        Bus.$emit('flash-message', {
                            message: 'Send create password emails is now running. ' +
                                'We will send you notification when the process completes'
                        });
                    })
                    .catch(() => {
                        Bus.$emit('server-error');
                    });
            }
        },

        reloadPanelists() {
            if (this.filterActive) {
                this.runFilter();
            } else if (this.searchActive) {
                this.runSearch();
            } else {
                Bus.$emit('flash-message', {message: 'Nothing to reload!', type: 'warning'});
            }
        },

        runSearch() {

            this.setSearchActive(true);

            this.searchPanelists()
                .then(response => {
                    if (this.panelists.length >= 100) {
                        Bus.$emit('flash-message', {message: 'Wow!! Lots of panelists found! Displaying the first 100 panelists!'});
                    }
                })
                .catch(error => {
                    Bus.$emit('flash-message', {message: 'OOPS! We failed to search!', type: 'error'});
                });
        },

        searchCleared() {
            this.clearMeta();
            this.setPanelists([]);
            this.setSearchActive(false);
        },

        selectColumnsClicked() {
            this.dataVariablePickerModel = true;
        },

        columnsSelected(variables) {

            this.setSelectedVariables(variables);
            this.buildHeaders();

            // If filter has been selected run
            if (this.filterActive) {
                this.runFilter();
            }

            if (this.searchActive) {
                this.runSearch();
            }
        },

        editPanelist(id) {
            this.$router.push({
                name: 'panelist-manager-edit',
                params: {id: id}
            });
        },

        deletePanelistClicked(id) {
            this.confirmationDialogModel = true;
            this.deletePanelistId = id;
        },

        deletePanelist() {
            if (this.deletePanelistId === null) {
                throw new Error('Delete panelist ID is null. This should never happen.');
            }

            this.setPanelistsLoading(true);

            this.deleteProfile(this.deletePanelistId)
                .then(() => {
                    Bus.$emit('flash-message', {
                        message: 'Panelist deleted!'
                    });
                })
                .catch((error) => {

                    if (error.response.status === 404) {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'This panelist is no longer found!'
                        });
                    } else {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Failed to delete the panelist!'
                        });
                    }
                })
                .finally(() => {
                    this.setPanelistsLoading(false);
                    this.deletePanelistId = null;
                });
        },

        confirmationDialogEntry(value) {
            if (value) this.deletePanelist();
        },

        buildHeaders() {
            let headers = [
                {
                    text: 'ID',
                    align: 'center',
                    sortable: false,
                    width: '1px'
                }
            ];
            this.selectedVariables.forEach(variable => {
                headers.push({
                    text: variable.name.replace('_', ' '),
                    align: 'left',
                    value: variable.uuid,
                    sortable: true
                });
            });
            headers.push({text: '', width: '40px', align: 'right', value: '', sortable: false});
            this.setHeaders(headers);
        },

        filtersChanged(filters) {

            // Only one filter can be active at one time
            this.clearActiveFilters();

            this.setFilters(filters);

            if (this.filterActive) {
                this.runFilter();
            } else {
                // Filters deselected clear meta
                this.clearMeta();
                this.setPanelists([]);
            }
        },

        runFilter() {
            this.filterPanelists()
                .then(response => {

                    console.log(response);

                    if (response.data.create_password) {
                        this.createPasswordBatchActionEnabled = true;
                    }

                    if (this.panelists.length >= 100) {
                        Bus.$emit('flash-message', {message: 'Wow!! Lots of panelists found! Displaying the first 100 panelists!'});
                    }
                })
                .catch(error => {
                    console.log(error);
                    Bus.$emit('flash-message', {message: 'OOPS! We failed to filter!', type: 'error'});
                });
        },

        infoIconClicked(id) {
            this.copyToClipboard(id);
            Bus.$emit('flash-message', {duration: 1500, message: 'ID copied to Clipboard!'});
        },

        closeWarning() {
            localStorage.warningAlertDismissed = JSON.stringify(true);
            this.warningAlertDismissed = true;
        }
    },

    mounted() {

        this.warningAlertDismissed = !!localStorage.getItem('warningAlertDismissed');

        // Load data variables
        this.getDataVariables()
            .then(() => {

                // Do not override headers if user navigates back to this page
                if (this.headers.length === 0) {

                    let defaults = [];

                    this.defaultSelected.forEach(name => {
                        defaults.push(this.dataVariables.find(item => item.name === name));
                    });

                    // Select defaults columns and builds an array of IDs to be queried
                    this.setSelectedVariables(defaults);

                    this.buildHeaders();
                }
            })
            .catch(() => {
                Bus.$emit('flash-message', {message: 'OOPS! Data Variables failed to load!', type: 'error'});
            });

        // Load sub panels
        this.$store.dispatch('SubPanels/loadSubPanels');
    },

    created() {
        if (!this.$store.state.PanelistManager) {
            this.$store.registerModule('PanelistManager', Store);
        }
    }
}

</script>
